import React, { useEffect, useMemo, useState } from "react";

const FLOORS = [
  { key: "ground", label: "Ground", url: "/floors/floor_ground 2.svg" },
  { key: "1", label: "Floor 1", url: "/floors/floor_1 2.svg" },
  { key: "2", label: "Floor 2", url: "/floors/floor_2 2.svg" },
];

function findRoomIdFromTarget(target, roomsById) {
  let el = target;
  while (el && el !== document && el.getAttribute) {
    const dataId = el.getAttribute("data-room-id");
    if (dataId && roomsById?.[dataId]) return dataId;

    const id = el.getAttribute("id");
    if (id && roomsById?.[id]) return id;

    el = el.parentNode;
  }
  return null;
}

function parseCapacityFromRoomName(name) {
  const m = String(name).match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : 0;
}

function buildRoomsByIdFromSvg(svgText, floorKey) {
  const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
  const nodes = Array.from(doc.querySelectorAll("[id]"));

  const rooms = {};
  for (const el of nodes) {
    const id = el.getAttribute("id");
    if (!id) continue;

    // Rooms look like: "Bagga 1", "Crow 2", "Shilstone 4"
    if (!/\s+\d+\s*$/.test(id)) continue;

    rooms[id] = {
      name: id,
      capacity: parseCapacityFromRoomName(id),
      floorKey,
    };
  }
  return rooms;
}

export default function RoomPlanPicker({
  onClose,
  selectedRoomIds,
  onChangeSelectedRoomIds,
  unavailableRoomIds = [],
  // OPTIONAL but recommended if you want BookingForm to know room capacities/names
  onRoomsByIdChange,
}) {  
  const [floorKey, setFloorKey] = useState("ground");
  const [svgText, setSvgText] = useState("");
  const [roomsById, setRoomsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const floor = useMemo(() => FLOORS.find((f) => f.key === floorKey) ?? FLOORS[0], [floorKey]);

  const unavailableSet = useMemo(() => new Set(unavailableRoomIds || []), [unavailableRoomIds]);
  const selectedSet = useMemo(() => new Set(selectedRoomIds || []), [selectedRoomIds]);

  // Load SVG + derive rooms from ids
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadErr("");
      setSvgText("");
      setRoomsById({});

      try {
        const res = await fetch(floor.url, { cache: "no-store" });
        if (!res.ok) throw new Error(`Failed to load ${floor.url} (${res.status})`);
        const txt = await res.text();

        if (cancelled) return;

        setSvgText(txt);

        const built = buildRoomsByIdFromSvg(txt, floorKey);
        setRoomsById(built);

        // Push rooms up to parent so BookingForm can show names/caps & compute pricing
        if (onRoomsByIdChange) {
          onRoomsByIdChange((prev) => ({ ...(prev || {}), ...built }));
        }
      } catch (e) {
        if (!cancelled) setLoadErr(e?.message || "Failed to load SVG");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [floor.url, floorKey, onRoomsByIdChange]);

  // Decorate SVG nodes
   // Decorate SVG nodes (selected fill + unavailable greyed out)
 useEffect(() => {
  if (!svgText) return;

  const root = document.getElementById("room-plan-svg-root");
  if (!root) return;

  const svg = root.querySelector("svg");
  if (!svg) return;

  // 1) Stop random SVG stuff (strokes, clip paths, text labels, etc) from stealing clicks
  svg.querySelectorAll("*").forEach((el) => {
    // Let only room elements re-enable this below
    el.style.pointerEvents = "none";
  });

  // 2) Now decorate ONLY the room elements, and re-enable pointer events for those
  const roomEls = svg.querySelectorAll("[id]");
  roomEls.forEach((el) => {
    const id = el.getAttribute("id");
    if (!id) return;

    const room = roomsById?.[id];
    if (!room) return; // not a real room id like "Bagga 1"

    const isUnavailable = unavailableSet.has(id);
    const isSelected = selectedSet.has(id);

    // Make this element actually clickable again
    el.style.pointerEvents = isUnavailable ? "none" : "auto";
    el.style.cursor = isUnavailable ? "not-allowed" : "pointer";

    el.style.transition =
      "opacity 120ms ease, filter 120ms ease, stroke 120ms ease, stroke-width 120ms ease, fill 120ms ease";

 if (isUnavailable) {
  el.style.pointerEvents = "none";
  el.style.cursor = "not-allowed";

  paintSvgElement(el, {
    fill: "#4b5563",
    stroke: "#1f2937",
    strokeWidth: 1.5,
    opacity: 1,
  });
} else {
  el.style.pointerEvents = "auto";
  el.style.cursor = "pointer";

  // reset if you want
  paintSvgElement(el, { opacity: 0.95 });
}

    if (isSelected) {
      el.style.stroke = "#111";
      el.style.strokeWidth = "3";
      if (!isUnavailable) el.style.fill = "#7dd3fc";
    } else {
      el.style.stroke = "transparent";
      el.style.strokeWidth = "0";
      el.style.fill = "";
    }
  });
}, [svgText, roomsById, unavailableSet, selectedSet]);

function paintSvgElement(el, { fill, stroke, strokeWidth, opacity }) {
  // If the element itself is a drawable shape, include it.
  const isShape = (node) =>
    ["path", "rect", "circle", "ellipse", "polygon", "polyline"].includes(
      node.tagName?.toLowerCase?.()
    );

  const targets = [];

  if (isShape(el)) targets.push(el);

  // Also paint any shape children (common when id is on a <g>)
  el.querySelectorAll?.("path, rect, circle, ellipse, polygon, polyline").forEach((n) => {
    targets.push(n);
  });

  // Apply styles to each drawable node
  targets.forEach((n) => {
    if (opacity != null) n.style.opacity = String(opacity);

    if (fill != null) {
      // style + attribute gives best chance of winning
      n.style.fill = fill;
      n.setAttribute("fill", fill);
    }

    if (stroke != null) {
      n.style.stroke = stroke;
      n.setAttribute("stroke", stroke);
    }

    if (strokeWidth != null) {
      n.style.strokeWidth = String(strokeWidth);
      n.setAttribute("stroke-width", String(strokeWidth));
    }
  });
}

  function toggleRoom(id) {
    if (!id) return;
    const room = roomsById?.[id];
    if (!room) return;
    if (unavailableSet.has(id)) return;

    const next = new Set(selectedRoomIds || []);
    if (next.has(id)) next.delete(id);
    else next.add(id);

    onChangeSelectedRoomIds?.(Array.from(next));
  }

  function onSvgClick(e) {
    const id = findRoomIdFromTarget(e.target, roomsById);
    if (!id) return;
    toggleRoom(id);
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "grid",
        placeItems: "center",
        zIndex: 9999,
        padding: 16,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div style={{ width: "min(1100px, 96vw)", background: "#fff", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: 12, display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid #eee" }}>
          <div style={{ fontWeight: 900 }}>Choose rooms</div>

          <div style={{ display: "flex", gap: 8, marginLeft: 10, flexWrap: "wrap" }}>
            {FLOORS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFloorKey(f.key)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #ddd",
                  background: f.key === floorKey ? "#111" : "#fff",
                  color: f.key === floorKey ? "#fff" : "#111",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Selected: <b>{selectedRoomIds?.length || 0}</b>
            </div>

            <button
              type="button"
              onClick={() => onChangeSelectedRoomIds?.([])}
              style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff", fontWeight: 800 }}
            >
              Clear
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff", fontWeight: 800 }}
            >
              Done
            </button>
          </div>
        </div>

        <div style={{ padding: 12 }}>
          {loading ? <div style={{ fontSize: 13, opacity: 0.85 }}>Loading floor plan…</div> : null}
          {loadErr ? <div style={{ fontSize: 13, color: "crimson" }}>{loadErr}</div> : null}

          <div
            id="room-plan-svg-root"
            onClick={onSvgClick}
            style={{
              border: "1px solid #eee",
              borderRadius: 10,
              overflow: "auto",
              maxHeight: "70vh",
              background: "#fafafa",
              padding: 8,
            }}
            dangerouslySetInnerHTML={{ __html: svgText }}
          />
        </div>

       {(selectedRoomIds || []).length > 0 ? (
  <div
    style={{
      padding: 12,
      borderTop: "1px solid #eee",
      background: "linear-gradient(180deg, rgba(125,211,252,0.18), rgba(0,0,0,0))",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <div style={{ fontWeight: 900, fontSize: 13 }}>
        Selected rooms <span style={{ opacity: 0.7 }}>({selectedRoomIds.length})</span>
      </div>

      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => onChangeSelectedRoomIds?.([])}
          style={{
            padding: "6px 10px",
            borderRadius: 10,
            border: "1px solid rgba(0,0,0,0.18)",
            background: "#fff",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Clear all
        </button>
      </div>
    </div>

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {selectedRoomIds.map((id) => {
        const room = roomsById?.[id];
        return (
          <span
            key={id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              borderRadius: 999,
              border: "1px solid rgba(0,0,0,0.22)",
              background: "#fff",
              boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
              fontSize: 13,
              fontWeight: 800,
            }}
            title={id}
          >
            <span>
              {room?.name ?? id}
              {room?.capacity ? (
                <span style={{ marginLeft: 8, fontWeight: 900, opacity: 0.8 }}>({room.capacity}p)</span>
              ) : null}
            </span>

            <button
              type="button"
              onClick={() => toggleRoom(id)}
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.18)",
                background: "#f6f6f6",
                cursor: "pointer",
                fontWeight: 900,
                lineHeight: "20px",
                padding: 0,
              }}
              aria-label={`Remove ${room?.name ?? id}`}
              title="Remove"
            >
              ×
            </button>
          </span>
        );
      })}
    </div>
  </div>
) : null}
      </div>
    </div>
  );
}